import { Skeleton } from '@mui/material';
import React, { FC, useEffect, useRef } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormPhoneInput } from 'src/components/ui/FormPhoneInput/FormPhoneInput';
import { ThemeSwitch } from 'src/components/ui/ThemeSwitch/ThemeSwitch';
import WhiteLabelsConfig from 'src/components/ui/WhiteLabelsConfig/WhiteLabelsConfig';
import { formRules, maxLengthRules } from 'src/constants/formConstants';
import { contactDetails } from 'src/constants/globalConstants';
import {
  changeSettings,
  editSupportInfo,
  getSupportInfo,
} from 'src/store/actions/authActions';
import { SupportInfo } from 'src/store/models/Auth';
import { WhiteLabelConfig } from 'src/store/models/WhiteLabel';
import { useFileSelector } from 'src/utils/useFileSelector';
import {
  useErrorSelector,
  useLoadingSelector,
} from 'src/utils/useLoadingSelector';
import { v4 as uuidv4 } from 'uuid';

import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { addModal, getDynamicSettings } from '../../../../features/globalSlice';
import { ModalType } from '../../../../store/models/Modal';
import Avatar from '../../../ui/Avatar/Avatar';
import Button from '../../../ui/Button/Button';
import { FormInput } from '../../../ui/FormInput/FormInput';
import PageHeader from '../../../ui/PageHeader/PageHeader';
import UploadPicture from '../../../ui/UploadPicture/UploadPicture';
import classes from './AdminSettings.module.scss';

export interface AdminSettingsFormData extends SupportInfo {
  title: string;
  whiteLabelsConfig: WhiteLabelConfig[];
}

const defaultValues: AdminSettingsFormData = {
  title: '',
  eMail: '',
  phone: '',
  website: '',
  whiteLabelsConfig: [],
};

const AdminSettings: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const imageId = useRef(uuidv4());

  const loading = useLoadingSelector({ actionType: getSupportInfo.type });
  const supportError = useErrorSelector<SupportInfo>({
    actionType: editSupportInfo.type,
  });

  const image = useFileSelector({ id: imageId.current })?.[0];
  const user = useAppSelector((state) => state.auth.user);
  const supportInfo = useAppSelector((state) => state.auth.supportInfo);
  const dynamicSettings = useAppSelector(
    (state) => state.global.dynamicSettings
  );
  const whiteLabels = dynamicSettings?.whiteLabelsConfig;

  const methods = useForm<AdminSettingsFormData>({
    mode: 'onChange',
    defaultValues,
  });
  const { control, handleSubmit, formState, reset, setValue, getValues } =
    methods;

  useEffect(() => {
    dispatch(getSupportInfo());
    dispatch(getDynamicSettings());
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (supportInfo) {
      reset({ ...getValues(), ...supportInfo });
    }
    // eslint-disable-next-line
  }, [supportInfo]);

  useEffect(() => {
    if (whiteLabels) {
      reset({ ...getValues(), whiteLabelsConfig: whiteLabels });
    }
    // eslint-disable-next-line
  }, [whiteLabels]);

  useEffect(() => {
    if (user) {
      setValue('title', user?.title);
    }
    // eslint-disable-next-line
  }, [user]);

  const onSubmit: SubmitHandler<AdminSettingsFormData> = (data) => {
    reset(data);

    dispatch(
      changeSettings({
        image: image,
        settingsData: data,
        dynamicSettings: data?.whiteLabelsConfig,
      })
    );
  };

  return (
    <div className={classes.Container}>
      <PageHeader
        title={t('common:settings')}
        backConfirmationText={
          formState.isDirty && t('common:changes_not_saved')
        }
        endNode={
          <div className={classes.HeaderRight}>
            <ThemeSwitch />
            <Button
              disabled={!(image || (formState?.isValid && formState?.isDirty))}
              className={classes.HeaderButton}
              type="submit"
              form="admin-settings-form"
            >
              {t('common:save_changes')}
            </Button>
          </div>
        }
      />
      <h4>{t('settings:company_logo')}</h4>
      <section>
        <UploadPicture
          imageComponent={
            <Avatar
              imageSize={128}
              showTitles={false}
              title={user?.title}
              image={image ? image?.url : user?.image}
            />
          }
          imageId={imageId.current}
          enableRemove={!!(image?.url || (user?.image && image?.url !== null))}
          className={classes.Picture}
        />
      </section>
      <FormProvider {...methods}>
        <form
          noValidate
          id="admin-settings-form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <h4>{t('settings:dynamic_settings')}</h4>
          <p>{t('settings:choose_manufacturer')}</p>
          <WhiteLabelsConfig originalValues={whiteLabels} />
          <h4>{t('settings:account_manager')}</h4>
          <p>{t('settings:account_manager_info')}</p>
          <section>
            <FormInput
              name="title"
              control={control}
              label={t('common:name')}
              placeholder={t('common:enter_name')}
              rules={{ ...formRules, ...maxLengthRules() }}
            />
          </section>
          <h4>{t('settings:support_info')}</h4>
          <p>{t('settings:support_info_warning')}</p>
          <section>
            {!loading ? (
              <>
                <FormInput
                  name="eMail"
                  control={control}
                  label={t('common:email')}
                  placeholder={t('common:enter_email')}
                  rules={{ ...formRules, ...maxLengthRules() }}
                  error={!!supportError?.eMail}
                  helperText={supportError?.eMail}
                />
                <FormPhoneInput
                  name="phone"
                  control={control}
                  label={t('common:phone')}
                  placeholder={t('common:enter_phone')}
                  rules={{ ...formRules, ...maxLengthRules() }}
                  error={!!supportError?.phone}
                  helperText={supportError?.phone}
                />
                <FormInput
                  name="website"
                  control={control}
                  label={t('common:website')}
                  placeholder={t('common:enter_website')}
                  rules={{ ...formRules, ...maxLengthRules() }}
                  error={!!supportError?.website}
                  helperText={supportError?.website}
                />
              </>
            ) : (
              <>
                <Skeleton animation="wave" variant="text" />
                <Skeleton animation="wave" variant="text" />
                <Skeleton animation="wave" variant="text" />
              </>
            )}
            <Button
              size="large"
              variant="outlined"
              onClick={() =>
                dispatch(
                  addModal({
                    id: '1',
                    title: t('settings:change_password'),
                    modalType: ModalType.CHANGE_PASSWORD,
                    primaryButtonText: t('common:confirm'),
                    secondaryButtonText: t('common:cancel'),
                    onBackdropClose: false,
                  })
                )
              }
            >
              {t('settings:change_password')}
            </Button>
          </section>
        </form>
      </FormProvider>
      <h4>
        {t('settings:contact_person_at', { company: contactDetails?.company })}
      </h4>
      <div className={classes.SupportInfo}>
        <p>
          {`${t('common:name')}: `}
          <span>{contactDetails?.name}</span>
        </p>
        <p>
          {`${t('common:email')}: `}
          <a href={`mailto: ${contactDetails?.email}`}>
            {contactDetails?.email}
          </a>
        </p>
      </div>
    </div>
  );
};

export default AdminSettings;
